import { Accordion, AccordionDetails, AccordionSummary, Box, Card, Typography } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Header } from '../../common/Header';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { refreshSubscriptionSection, setSubscriptionSyncPending } from '../../../store/subscription-slice/subscription-slice';
import { CurrentSubscriptionSection } from './CurrentSubscriptionSection';
import { InvoicesSection } from './InvoicesSection';
import { PlansSection } from './PlansSection';

const WEBHOOK_SYNC_FALLBACK_MS = 10000;

export function SubscriptionsSection() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const syncFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const profileLoading = useAppSelector((state) => state.profile.loading);
    const userInfo = useAppSelector((state) => state.profile.userInfo);
    const loadingBillingState = useAppSelector((state) => state.subscription.loadingBillingState);
    const subscriptionSyncPending = useAppSelector((state) => state.subscription.subscriptionSyncPending);
    const isSubscriptionExemptUser = userInfo?.isSubscriptionExempt === true;

    const isSubscriptionDetailsLoading = profileLoading || loadingBillingState;

    const clearSyncFallbackTimeout = useCallback(() => {
        if (syncFallbackTimeoutRef.current) {
            clearTimeout(syncFallbackTimeoutRef.current);
            syncFallbackTimeoutRef.current = null;
        }
    }, []);

    const startSubscriptionSyncTracking = useCallback(() => {
        clearSyncFallbackTimeout();
        dispatch(setSubscriptionSyncPending(true));

        syncFallbackTimeoutRef.current = setTimeout(() => {
            void dispatch(refreshSubscriptionSection({ showLoader: false })).finally(() => {
                dispatch(setSubscriptionSyncPending(false));
            });
        }, WEBHOOK_SYNC_FALLBACK_MS);
    }, [clearSyncFallbackTimeout, dispatch]);

    useEffect(() => {
        if (!subscriptionSyncPending) {
            clearSyncFallbackTimeout();
        }
    }, [clearSyncFallbackTimeout, subscriptionSyncPending]);

    useEffect(() => {
        void dispatch(refreshSubscriptionSection({ showLoader: true }));
        return () => {
            clearSyncFallbackTimeout();
        };
    }, [clearSyncFallbackTimeout, dispatch]);

    return (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
            {!isSubscriptionExemptUser && (
                <Card elevation={3} sx={{ borderRadius: 2.5, border: '1px solid var(--selected-table-row-color)' }}>
                    <Header title={t('PROFILE_SUBSCRIPTION_DETAILS')} />
                    <Box sx={{ p: { xs: 1.5, md: 2.25 } }}>
                        <CurrentSubscriptionSection
                            loading={isSubscriptionDetailsLoading}
                            onSubscriptionMutationCompleted={startSubscriptionSyncTracking}
                        />
                        {!isSubscriptionDetailsLoading && (
                            <Accordion
                                disableGutters
                                defaultExpanded={false}
                                elevation={0}
                                sx={{
                                    mt: 2,
                                    borderRadius: 2.5,
                                    border: '1px solid var(--selected-table-row-color)',
                                    overflow: 'hidden',
                                    '&::before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        px: { xs: 1.5, md: 2 },
                                        backgroundColor: 'action.hover',
                                        borderBottom: '1px solid var(--selected-table-row-color)',
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {t('PROFILE_INVOICES_TITLE')}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <InvoicesSection showHeader={false} />
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Box>
                </Card>
            )}

            {!isSubscriptionExemptUser && (
                <Card elevation={3} sx={{ borderRadius: 2.5, border: '1px solid var(--selected-table-row-color)' }}>
                    <Header title={t('PROFILE_BILLING_TITLE')} />
                    <PlansSection />
                </Card>
            )}
        </Box>
    );
}
